import { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { SearchInput } from "../components";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
// Reuse the History layout so both screens look identical.
import styles from "../history/history.module.scss";
import { TransactionFilters, DEFAULT_FILTERS, consumePendingFilters, setPendingFilters } from "../history/filter-page/history.filters";
import { FilterIcon } from "../../../assets/customer-settings-icons";
import { useGetPrepaidStatementOfAccountQuery, useGetPostpaidStatementOfAccountQuery } from "../../../service/vending-service";
import { PaymentHistoryListItem } from "./components/PaymentHistoryListItem";
import { StatementOfAccountTransaction } from "../../../model/vending/vending-interfaces";
import { ConsumerType } from "../../../model/energy-profile";
import { formatCurrency } from "../../../utils";
import { formatDate, formatTime } from "../history/history.utils";
import { transactionTypeLabel, transactionMoney } from "./historical-payment.utils";

// Params selected on the Account Activities screen, passed via route state.
interface HistoricalPaymentNavState {
	meterId?: string;
	consumerType?: ConsumerType;
	startDate?: Date | null;
	endDate?: Date | null;
}

// Backend expects date-only (yyyy-MM-dd); build it from local parts to avoid a UTC off-by-one.
const toDateOnly = (date: Date | null | undefined): string => {
	if (!date) return "";
	const d = new Date(date);
	const month = `${d.getMonth() + 1}`.padStart(2, "0");
	const day = `${d.getDate()}`.padStart(2, "0");
	return `${d.getFullYear()}-${month}-${day}`;
};

// Search is client-side over every visible field; the date range refetches from the backend (see below).
const buildSearchText = (p: StatementOfAccountTransaction, consumerType: ConsumerType | undefined, t: TFunction): string => {
	const money = transactionMoney(p, consumerType);
	return [
		// Include both the raw number ("5000") and the formatted value ("₦5,000.00") so either matches.
		money ? String(money.amount) : "",
		money ? formatCurrency(money.amount, { currency: money.currency }) : "",
		p.transactionDate ? `${formatDate(p.transactionDate)} ${formatTime(p.transactionDate)}` : "",
		transactionTypeLabel(p.transactionType, consumerType, t),
		p.description ?? "",
		p.referenceId,
	]
		.join(" ")
		.toLowerCase();
};

export const HistoricalPaymentPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchValue, setSearchValue] = useState("");
	// Pick up filters applied on the (shared) filter page when returning to this screen.
	const [activeFilters, setActiveFilters] = useState<TransactionFilters>(() => consumePendingFilters() ?? DEFAULT_FILTERS);

	// Meter + date range chosen on the Account Activities screen drive the server query.
	const { meterId, consumerType, startDate, endDate } = (location.state ?? {}) as HistoricalPaymentNavState;
	const isPostpaid = consumerType === ConsumerType.Postpaid;
	// The (prepaid) period filter overrides the initial range; changing it re-runs the query below.
	const from = toDateOnly(activeFilters.startDate ?? startDate);
	const to = toDateOnly(activeFilters.endDate ?? endDate);

	// The endpoint is chosen by meter type; only the relevant query runs (the other is skipped).
	const prepaid = useGetPrepaidStatementOfAccountQuery(
		{ MeterId: meterId ?? "", From: from, To: to },
		{ skip: !meterId || isPostpaid || !from || !to }
	);
	const postpaid = useGetPostpaidStatementOfAccountQuery({ MeterId: meterId ?? "" }, { skip: !meterId || !isPostpaid });
	const { data, isFetching, error, refetch } = isPostpaid ? postpaid : prepaid;

	const hasActiveFilters = useMemo(
		() =>
			activeFilters.period !== DEFAULT_FILTERS.period ||
			activeFilters.startDate !== DEFAULT_FILTERS.startDate ||
			activeFilters.endDate !== DEFAULT_FILTERS.endDate,
		[activeFilters]
	);

	const payments = useMemo(() => {
		const rows = data?.transactions ?? [];
		const term = searchValue.trim().toLowerCase();
		if (!term) return rows;
		return rows.filter(p => buildSearchText(p, consumerType, t).includes(term));
	}, [data?.transactions, searchValue, consumerType, t]);

	// Only prepaid transactions have a detail endpoint; postpaid rows are not clickable.
	const handlePaymentClick = isPostpaid
		? undefined
		: (payment: StatementOfAccountTransaction) => navigate(`${payment.referenceId}`, { state: { transaction: payment, consumerType } });

	return (
		<CustomerPageLayout showBackButton className={styles.historyLayout} title={t("history.web.historicalPayment")} onBack={() => navigate(-1)}>
			<div className={styles.pageContainer}>
				<div className={styles.historyHeader}>
					<div className={styles.searchContainer}>
						<SearchInput
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							onClear={() => setSearchValue("")}
							placeholder={t("history.web.searchPlaceholder")}
							name="paymentSearch"
							className={styles.search}
						/>
						{/* Period filter is only available for prepaid (postpaid has no date range). */}
						{!isPostpaid && (
							<button
								className={styles.filterBtn}
								onClick={() => {
									setPendingFilters(activeFilters);
									navigate("filter");
								}}
							>
								<FilterIcon className={styles.filterIcon} />
								{hasActiveFilters && <span className={styles.filterBadge} />}
							</button>
						)}
					</div>
				</div>

				<div className={styles.transactionListWrapper}>
					<div className={styles.transactionList}>
						{payments.length > 0 ? (
							payments.map(payment => (
								<PaymentHistoryListItem key={payment.referenceId} transaction={payment} consumerType={consumerType} onClick={handlePaymentClick} />
							))
						) : error ? (
							<div className={styles.errorEmpty}>
								<p>{t("history.web.failedToLoadPayments")}</p>
								<button className={styles.retryButton} onClick={() => refetch()}>
									{t("energyManagement.retry")}
								</button>
							</div>
						) : isFetching ? (
							<div className={styles.loadingMore}>
								<div className={styles.spinner} />
								<span>{t("history.web.loadingPayments")}</span>
							</div>
						) : (
							<div className={styles.emptyState}>
								<p>{t("history.web.noPaymentsFound")}</p>
								{(searchValue || hasActiveFilters) && (
									<>
										<p className={styles.emptyStateHint}>{t("history.web.tryDifferentSearchOrFilters")}</p>
										<div className={styles.clearActions}>
											<button
												className={styles.clearSearchButton}
												onClick={() => {
													setSearchValue("");
													setActiveFilters(DEFAULT_FILTERS);
												}}
											>
												{t("history.clearAll")}
											</button>
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</CustomerPageLayout>
	);
};
